import type * as React from 'react';
import { ChevronRight } from 'lucide-react';
import { cn } from '../../lib/cn.ts';

export interface SettingsRowProps extends Omit<
  React.ComponentProps<'div'>,
  'style' | 'children'
> {
  /*
   * Usually a string. A node is allowed so a row can carry more than words —
   * a gesprekstype's name wears the coloured pill it wears elsewhere.
   */
  label: React.ReactNode;
  /*
   * Usually a sentence. A node is allowed so a row can say what *state* it is
   * in — a connected calendar reads "Connected" in green here — which belongs
   * under the label with the thing it describes, not stranded out in the
   * control column next to the buttons.
   */
  description?: React.ReactNode;
  /*
   * Short notes said right after the label, on its line. Kept out of the open
   * button, so a row opened by name is announced by its name alone.
   */
  labelAside?: React.ReactNode;
  /* The control: a switch, a select, a segmented control, an editable value. */
  children?: React.ReactNode;
  /*
   * Makes the whole row open what it is about, with a chevron to say so. The
   * label is the button; the handle and the control stay clickable on their own.
   */
  onOpen?: () => void;
  /*
   * The grip a row is picked up by, before the label. Only rows in a list
   * somebody arranged carry one — a setting has no order to keep.
   */
  handle?: React.ReactNode;
  /*
   * Where the row sits while it is being dragged, as a CSS transform, and how
   * it travels once it is let go. Both come from the drag library the page
   * uses: a row's position under a pointer is not a style anybody authors,
   * which is why it is the one thing passed in rather than written here.
   */
  dragTransform?: string;
  dragTransition?: string;
  /* True for the one row being carried, so it lifts off the rows it crosses. */
  dragging?: boolean;
}

/*
 * One setting: what it is on the left, how you change it on the right.
 *
 * The anatomy is fixed by props rather than by composition on purpose — it is
 * what keeps every settings page identical, and it makes the "no icons in
 * settings rows" rule structural instead of a thing to remember.
 *
 * Personal settings take effect immediately, so there is no save bar to leave
 * room for. Only org-wide templates earn an explicit save.
 */
export function SettingsRow({
  label,
  labelAside,
  description,
  className,
  children,
  onOpen,
  handle,
  dragTransform,
  dragTransition,
  dragging,
  ...props
}: SettingsRowProps) {
  const labelClass = 'text-md font-semibold text-ink';

  return (
    <div
      data-slot='settings-row'
      style={{ transform: dragTransform, transition: dragTransition }}
      className={cn(
        'flex items-center gap-4 border-t border-fill px-5.5 py-4 first:border-t-0',
        /*
         * Side by side is a desktop luxury. On a phone the label and the
         * control were fighting over one line — the control does not shrink, so
         * a text field simply grew straight through the label and off the
         * screen. Stacking gives each of them the full width.
         * A row that opens holds no field, and its chevron belongs beside it.
         */
        onOpen
          ? 'relative transition-colors hover:bg-fill-faint'
          : 'max-mobile:flex-col max-mobile:items-stretch max-mobile:gap-2',
        /*
         * The row being carried rides over the ones it crosses, and takes the
         * card's own background with it — the rows underneath would otherwise
         * read straight through it.
         */
        dragging && 'relative z-10 bg-surface shadow-raise',
        className,
      )}
      {...props}
    >
      {onOpen && handle ? (
        <div className='relative z-1 flex shrink-0'>{handle}</div>
      ) : (
        handle
      )}
      <div className='min-w-0 flex-1'>
        <div className='flex flex-wrap items-center gap-x-3 gap-y-0.5'>
          {onOpen ? (
            <button
              type='button'
              data-slot='settings-row-open'
              onClick={onOpen}
              className={cn(
                labelClass,
                'cursor-pointer text-left outline-none',
                /* The button's hit area, and its focus ring, are the whole row. */
                'after:absolute after:inset-0 after:content-[""]',
                'focus-visible:after:outline-2 focus-visible:after:-outline-offset-2 focus-visible:after:outline-ink-faint',
              )}
            >
              {label}
            </button>
          ) : (
            <div className={labelClass}>{label}</div>
          )}
          {labelAside}
        </div>
        {description && <p className='mt-0.5 text-sm text-ink-muted'>{description}</p>}
      </div>
      {/*
       * Never shrinks on desktop; always full width once stacked. Capped at
       * just over half the row, because a content-sized control — a textarea
       * with `field-sizing: content` — is otherwise as wide as its longest
       * line and crushes the label column to one word per line.
       */}
      {children !== undefined && (
        <div
          className={cn(
            'shrink-0',
            onOpen ? 'relative z-1' : 'mobile:max-w-[55%] max-mobile:w-full',
          )}
        >
          {children}
        </div>
      )}
      {onOpen && (
        <ChevronRight aria-hidden='true' className='size-3.5 shrink-0 text-ink-muted' />
      )}
    </div>
  );
}

export type SettingsValueProps = Omit<React.ComponentProps<'span'>, 'style'>;

/* A value the user cannot change here — it comes from somewhere else. */
export function SettingsValue({ className, ...props }: SettingsValueProps) {
  return (
    <span
      data-slot='settings-value'
      className={cn('text-sm font-semibold text-ink-muted', className)}
      {...props}
    />
  );
}
